// useHistogramData.ts
import { useMemo, useState, useEffect } from "react";
import { getConversationalSuccessHistogram } from "../api/controllers/conversationalSuccessHistogram";

type Args = {
    applicationId?: string; // For single app mode
    applicationIds?: string[]; // For organization-level (multiple apps)
    startDate: string; // ISO "YYYY-MM-DD"
    endDate: string;   // ISO "YYYY-MM-DD"
    categories: string[];
    tags: string[];
    interval: number
};

export function useHistogramData({ applicationId, applicationIds, startDate, endDate, categories, tags, interval }: Args) {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const applicationIdsKey = useMemo(() => applicationIds?.join(',') ?? '', [applicationIds]);
    const categoriesKey = useMemo(() => categories?.join(',') ?? '', [categories]);
    const tagsKey = useMemo(() => tags?.join(',') ?? '', [tags]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // If multiple application IDs provided, fetch and aggregate
                if (applicationIds && applicationIds.length > 0) {
                    const promises = applicationIds.map(appId =>
                        getConversationalSuccessHistogram(
                            appId,
                            startDate,
                            endDate,
                            interval,
                            categories,
                            tags
                        )
                    );

                    const results = await Promise.all(promises);

                    // Aggregate data from all apps
                    const aggregatedData = new Map<string, { date: string; count: number; key: string }>();

                    results.forEach(result => {
                        if (result.data && Array.isArray(result.data)) {
                            result.data.forEach((datum: any) => {
                                const key = datum.date || datum.key;
                                if (aggregatedData.has(key)) {
                                    const existing = aggregatedData.get(key)!;
                                    existing.count += datum.count || 0;
                                } else {
                                    aggregatedData.set(key, {
                                        date: datum.date,
                                        count: datum.count || 0,
                                        key: datum.key || datum.date
                                    });
                                }
                            });
                        }
                    });

                    setData(Array.from(aggregatedData.values()).sort((a, b) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    ));
                }
                // Single application mode
                else if (applicationId) {
                    const result = await getConversationalSuccessHistogram(
                        applicationId,
                        startDate,
                        endDate,
                        interval,
                        categories,
                        tags
                    );
                    setData(result.data);
                } else {
                    setData([]);
                }
            } catch (err) {
                setError(err instanceof Error ? err : new Error('An error occurred'));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [applicationId, applicationIdsKey, startDate, endDate, categoriesKey, tagsKey, interval]);

    return { data, loading, error }
}